import { call, select, takeLatest } from 'redux-saga/effects';
import {
  fetchReaderTask,
  fetchParentReaderTask,
  saveReaderTask,
  saveParentReaderTask,
  startReaderTask,
  startParentReaderTask,
  completeReaderTask,
  completeParentReaderTask,
  fetchReaderDICOMResource as fetchReaderDICOMResourceAPI,
  fetchReadingDicomSeries,
  CompleteParentReaderTaskProps,
  ReaderParentTaskFetchProps,
  SaveParentReaderTaskProps,
} from 'src/api/readerTask';
import { actions } from './readerTask.slice';
import { Resp, dispatch, getUrlParams } from '../helpers';
import {
  ReaderTaskCompleteAction,
  ReaderTaskFetchAction,
  ReaderTaskSaveAction,
  ReaderTaskStartAction,
} from './readerTask.types';
import { runToast } from '../tools';
import { AxiosError } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  actions as readerCacheActions,
  selectCachedSubtask,
  selectCachedDicomResource,
  selectIsReaderCacheActive,
} from '../readerCache/readerCache.slice';
import {
  isReaderCacheEnabled,
  resolveTaskScoringCode,
  selectIsCacheableScoringForm,
} from '../readerCache/readerCache.gate';
import type { ReaderTaskType } from '@dynamika-shared/types/reader';

/**
 * Fetch every study of a grouped reading, without ever blocking the viewer
 *
 * Plain terms
 * -----------
 * Asks the reading as a whole which images it has, so earlier visits can be
 * shown beside the one being scored. If that question cannot be answered, the
 * viewer still opens on the current visit — the reader loses the prior visits,
 * not the reading.
 *
 * Technical
 * ---------
 * Called through `yield call(...)` from `fetchReaderNeededDataWorker`, inside
 * the same `all(...)` as the task fetches, so it must resolve rather than
 * reject: a rejection there would abort the whole leg and leave the viewer
 * unopened (3.9). The empty list it stores on failure is the exact signal
 * `prepareReaderStudy` reads as "use the single-visit path" (D7-7712).
 */
export function* fetchReadingDicomStudiesWorker(action: ReaderTaskFetchAction) {
  try {
    const response: Resp<typeof fetchReadingDicomSeries> = yield call(
      fetchReadingDicomSeries,
      action.payload
    );

    const studies = response?.data ?? [];

    yield dispatch(
      actions.fetchReadingDicomStudiesSuccess({
        taskId: action.payload.taskId,
        studies,
      })
    );

    return studies;
  } catch (error) {
    console.error('Error occur with reading DICOM studies', error);

    yield dispatch(actions.fetchReadingDicomStudiesFailure(error as Error));

    return [];
  }
}

export function* fetchReaderTaskWorker(action: ReaderTaskFetchAction) {
  const { taskId, parentTaskId } = action.payload;
  const cacheParentId = parentTaskId ?? taskId;

  const cacheEnabled: boolean = yield call(isReaderCacheEnabled);

  if (cacheEnabled) {
    const cached: ReaderTaskType | undefined = yield select(
      selectCachedSubtask,
      cacheParentId,
      taskId
    );

    if (cached) {
      yield dispatch(actions.fetchReaderTaskSuccess(cached));
      return cached;
    }
  }

  try {
    let response: Resp<typeof fetchReaderTask> | Resp<typeof fetchParentReaderTask>;

    if (parentTaskId) {
      response = yield call(fetchParentReaderTask, action.payload as ReaderParentTaskFetchProps);
    } else {
      response = yield call(fetchReaderTask, action.payload);
    }

    if (cacheEnabled) {
      const isCacheableForm: boolean = yield select(
        selectIsCacheableScoringForm,
        resolveTaskScoringCode(response.data)
      );

      if (isCacheableForm) {
        yield dispatch(
          readerCacheActions.setSubtask({
            parentTaskId: cacheParentId,
            subTaskId: taskId,
            data: response.data,
          })
        );
      }
    }

    yield dispatch(actions.fetchReaderTaskSuccess(response.data));
    return response.data;
  } catch (error) {
    console.error('Error occur with reader data fetching', error);

    yield runToast('error', 'Error occur with reader data fetching');
    yield dispatch(actions.fetchReaderTaskFailure(error));
    return error;
  }
}

export function* startReaderTaskWorker(action: ReaderTaskStartAction) {
  const { taskId, subTaskId } = yield call(getUrlParams);
  const shouldRefetch = action.payload.shouldRefetch ?? true;

  /**
   * Choose the parent+child vs. single-segment start endpoint
   *
   * Plain terms
   * -----------
   * Opening a grouped reading (e.g. a RANO patient with several visits) first
   * sends the reader to a visit page, so the address bar always carries a
   * visit (sub-task) id. When we then start the *parent* reading, that visit
   * id in the URL is NOT the thing we are starting. Trusting the URL blindly
   * made us ask the server to start "parent/parent", which it rejects with a
   * 404 and shows the reader an error — so the fix is to start the parent by
   * its own id alone.
   *
   * Technical
   * ---------
   * Only treat this as a sub-task start when the action's own `taskId` is the
   * URL's `subTaskId` (the genuine child-start case → `startParentReaderTask`
   * → `/reader/tasks/{parentTaskId}/{taskId}/start`). For a parent/standalone
   * start `payload.taskId` is the parent id (≠ URL `subTaskId`), so fall
   * through to `startReaderTask` → `/reader/tasks/{taskId}/start`. The previous
   * `if (subTaskId)` keyed off the URL alone, so re-entering `handleGroupTaskSaga`
   * for the parent while the URL already pointed at a visit mis-built the call
   * as `/reader/tasks/{parentId}/{parentId}/start`.
   *
   * NOTE: `saveReaderTaskWorker`/`completeReaderTaskWorker` below still use the
   * bare `if (subTaskId)` URL check. They are safe today (save/complete always
   * originate from a page already rendered inside the sub-task, so the URL's
   * subTaskId IS the task being acted on), but should be aligned with this
   * payload-aware check if a parent-level save/complete is ever added.
   */
  const isSubTaskStart = !!subTaskId && action.payload.taskId === subTaskId;

  try {
    if (isSubTaskStart) {
      action.payload.parentTaskId = taskId;
      yield call(startParentReaderTask, action.payload as ReaderParentTaskFetchProps);
    } else {
      yield call(startReaderTask, action.payload);
    }
    yield dispatch(actions.startReaderTaskSuccess('ok'));

    if (shouldRefetch) {
      yield call(
        fetchReaderTaskWorker,
        actions.fetchReaderTask(action.payload)
      );
    }
  } catch (error) {
    console.error('Error occur with reader data start', error);

    yield runToast('error', 'Error occur with reader data start');
    yield dispatch(actions.startReaderTaskFailure(error));
  }
}

export function* saveReaderTaskWorker(action: ReaderTaskSaveAction) {
  const { taskId, subTaskId } = yield call(getUrlParams);
  try {
    if (subTaskId) {
      action.payload.parentTaskId = taskId;
      yield call(saveParentReaderTask, action.payload as SaveParentReaderTaskProps);
    } else {
      yield call(saveReaderTask, action.payload);
    }

    yield dispatch(
      actions.saveReaderTaskSuccess({
        status: 'ok',
        shouldRedirect: action.payload.shouldRedirect,
      })
    );
    yield runToast('success', 'Form data has been saved successfully');
  } catch (error) {
    console.error('Error occur with reader data saving', error);

    yield runToast('error', 'Error occur with reader data saving');
    yield dispatch(actions.saveReaderTaskFailure(error));
  }
}

export function* completeReaderTaskWorker(action: ReaderTaskCompleteAction) {
  const { taskId, subTaskId } = yield call(getUrlParams);
  try {
    if (subTaskId) {
      action.payload.parentTaskId = taskId;
      yield call(completeParentReaderTask, action.payload as CompleteParentReaderTaskProps);
    } else {
      yield call(completeReaderTask, action.payload);
    }

    // Completing a sub-task changes the parent's childTasks statuses (and the
    // next visit's lesion-copy state). The parent is cached keyed by its own id
    // and is NOT invalidated on same-parent visit switches, so the next visit
    // load would otherwise read a stale not-COMPLETED status for this visit and
    // get wrongly blocked with "You can not load this task until finish previous
    // task". Drop the whole parent bucket so the subsequent load refetches fresh.
    // Invalidation is intentionally gated by slice presence only, not by the
    // viewer-route gate: dropping stale data must work from any route.
    if (subTaskId) {
      const isReaderCacheActive: boolean = yield select(selectIsReaderCacheActive);
      if (isReaderCacheActive) {
        yield dispatch(
          readerCacheActions.flushParentTask({ parentTaskId: taskId })
        );
      }
    }

    yield dispatch(
      actions.completeReaderTaskSuccess({
        status: 'ok',
        shouldRedirect: action.payload.shouldRedirect,
      })
    );

    // Refetch the reader task after successful submit to refresh form state
    // Do it only when we stay on the page (no redirect expected)
    if (!action.payload.shouldRedirect) {
      yield dispatch(
        actions.fetchReaderTask({
          clinicalTrialId: action.payload.clinicalTrialId,
          parentTaskId: action.payload.parentTaskId,
          taskId: action.payload.taskId,
          shouldRefetch: true,
        })
      );
    }

    yield runToast('success', 'Form data has been signed successfully');
    return;
  } catch (error) {
    const err = error as AxiosError<{
      detail: string;
    }>;

    if (err.response?.status === 403) {
      yield runToast('error', err.response?.data?.detail);
    } else {
      yield runToast('error', 'Error occur with reader data signing');
    }

    console.error('Error occur with reader complete form', error);
    yield dispatch(actions.completeReaderTaskFailure(error));
    return error;
  }
}

export function* fetchReaderDICOMResourceWorker(
  action: PayloadAction<{ clinicalTrialId: string; endpoint?: string }>
) {
  const { clinicalTrialId } = action.payload;

  const cached: string | null | undefined = yield select(
    selectCachedDicomResource,
    clinicalTrialId
  );

  if (cached !== undefined) {
    yield dispatch(actions.fetchReaderDICOMResourceSuccess(cached as string));
    return cached;
  }

  try {
    const data: Resp<typeof fetchReaderDICOMResourceAPI> = yield call(
      fetchReaderDICOMResourceAPI,
      action.payload.clinicalTrialId,
      action.payload.endpoint
    );

    const url = data?.url ?? null;
    const isReaderCacheActive: boolean = yield select(selectIsReaderCacheActive);
    if (isReaderCacheActive) {
      yield dispatch(
        readerCacheActions.setDicomResource({ clinicalTrialId, data: url })
      );
    }
    yield dispatch(actions.fetchReaderDICOMResourceSuccess(url));
  } catch (error) {
    console.error('Error occur with reader data fetching', error);

    yield runToast('error', 'Error occur with reader data fetching');
    yield dispatch(actions.fetchReaderDICOMResourceFailure(error));
    return error;
  }
}

export function* readerTaskSaga() {
  yield takeLatest(actions.fetchReaderTask.type, fetchReaderTaskWorker);
  yield takeLatest(actions.startReaderTask.type, startReaderTaskWorker);
  yield takeLatest(actions.saveReaderTask.type, saveReaderTaskWorker);
  yield takeLatest(actions.completeReaderTask.type, completeReaderTaskWorker);
  yield takeLatest(
    actions.fetchReaderDICOMResource.type,
    fetchReaderDICOMResourceWorker
  );
}
